import { useRouter } from '@tanstack/react-router'
import posthog from 'posthog-js'
import { type ReactNode, useEffect } from 'react'

// import { trackEvent } from '@/lib/analytics' // Not used in this file

// Initialize PostHog
if (typeof window !== 'undefined') {
  const apiKey = import.meta.env.VITE_POSTHOG_KEY
  const apiHost = import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com'

  if (apiKey) {
    posthog.init(apiKey, {
      api_host: apiHost,
      capture_pageview: false, // We'll manually track pageviews in router
      capture_pageleave: true,
      autocapture: false, // Manual event tracking for better control
    })
  }
}

export function PostHogProvider({ children }: { children: ReactNode }) {
  const router = useRouter()

  useEffect(() => {
    if (!router || typeof router.subscribe !== 'function') {
      return
    }

    // Track page views on route changes
    const unsubscribe = router.subscribe('onLoad', ({ toLocation }) => {
      if (posthog.__loaded) {
        posthog.capture('$pageview', {
          $current_url: toLocation.href,
        })
      }
    })

    return () => unsubscribe()
  }, [router])

  return <>{children}</>
}

// Factory function for creating PostHog provider (for testing and flexibility)
export function createPostHogProvider() {
  return function PostHogProviderWrapper({ children }: { children: ReactNode }) {
    const router = useRouter()

    useEffect(() => {
      if (!router || typeof router.subscribe !== 'function') {
        return
      }

      // Track page views on route changes
      const unsubscribe = router.subscribe('onLoad', ({ toLocation }) => {
        if (posthog.__loaded) {
          posthog.capture('$pageview', {
            $current_url: toLocation.href,
          })
        }
      })

      return () => unsubscribe()
    }, [router])

    return <>{children}</>
  }
}

// Convenience hook for using PostHog
export function usePostHog() {
  return posthog
}
